import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Icon, Block } from "../../../components/Component";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import LogoDark from "../../../images/logo-dark2x.png";
import { Alert, Spinner } from "reactstrap";
import { API_BASE_URL, getPublicUrl } from "../../../utils/Utils";
import { Link } from "react-router-dom";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { invoiceDisplayId, formatMoneyAmount, patientDisplayName } from "./invoiceHelpers";

const InvoicePrint = ({ match }) => {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    const id = match.params.id;
    if (!id || !token) {
      setLoading(false);
      setError(!token ? "Not signed in" : "Invalid invoice");
      return undefined;
    }
    const ac = new AbortController();
    axios
      .get(`${API_BASE_URL}/api/v1/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ac.signal,
      })
      .then((res) => setInv(res.data.data || null))
      .catch((err) => {
        if (!isAbortError(err)) {
          setInv(null);
          setError(err.response?.data?.message || err.message || "Invoice not found");
        }
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [match.params.id, token]);

  useEffect(() => {
    if (!loading && inv) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [loading, inv]);

  const lines = inv && Array.isArray(inv.lines) ? inv.lines : [];
  const createdLabel = inv?.createdAt ? new Date(inv.createdAt).toLocaleString("en-GB") : "—";
  const partnerName = inv?.medicalPartner?.organization_name || "—";
  const billing = inv?.medicalPartner?.billingProfile;
  const addressLine =
    billing &&
    [billing.billingAddressLine1, billing.billingCity, billing.billingPostalCode, billing.billingCountry]
      .filter(Boolean)
      .join(", ");

  return (
    <div className="bg-white">
      <Head title="Invoice Print"></Head>
      <Content>
        {loading && (
          <div className="p-5 text-center">
            <Spinner color="primary" />
          </div>
        )}
        {!loading && error && (
          <Alert color="danger" className="m-3">
            {error}
          </Alert>
        )}
        {!loading && inv && (
          <Block>
            {(inv.status === "draft" || !inv.invoiceNumber) && (
              <Alert color="warning" className="mb-2">
                Provisional browser print — drafts are watermarked on the official PDF. Use invoice detail →{" "}
                <Link to={getPublicUrl(`invoice-details/${inv._id}`)}>Download PDF</Link>.
              </Alert>
            )}
            <div className="invoice invoice-print">
              <div className="invoice-action">
                <Button
                  size="lg"
                  color="primary"
                  outline
                  className="btn-icon btn-white btn-dim"
                  type="button"
                  onClick={() => window.print()}
                >
                  <Icon name="printer-fill"></Icon>
                </Button>
              </div>
              <div className="invoice-wrap">
                <div className="invoice-brand text-center">
                  <img src={LogoDark} alt="" />
                </div>

                <div className="invoice-head">
                  <div className="invoice-contact">
                    <span className="overline-title">Bill to</span>
                    <div className="invoice-contact-info">
                      <h4 className="title">{patientDisplayName(inv.patient)}</h4>
                      <ul className="list-plain">
                        {inv.patient && inv.patient.email && (
                          <li>
                            <Icon name="mail"></Icon>
                            <span>{inv.patient.email}</span>
                          </li>
                        )}
                        {partnerName !== "—" && (
                          <li>
                            <Icon name="building"></Icon>
                            <span>Partner: {partnerName}</span>
                          </li>
                        )}
                        {addressLine && (
                          <li>
                            <Icon name="map-pin-fill"></Icon>
                            <span>{addressLine}</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  <div className="invoice-desc">
                    <h3 className="title">Invoice</h3>
                    <ul className="list-plain">
                      <li className="invoice-id">
                        <span>Reference</span>:<span>{invoiceDisplayId(inv)}</span>
                      </li>
                      <li className="invoice-date">
                        <span>Date</span>:<span>{createdLabel}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="invoice-bills">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th className="w-60">Description</th>
                          <th>Unit</th>
                          <th>Qty</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.length > 0 ? (
                          lines.map((line) => (
                            <tr key={line._id || `${line.description}-${line.lineTotal}`}>
                              <td>{line.description}</td>
                              <td>{formatMoneyAmount(line.unitAmount, inv.currency)}</td>
                              <td>{line.quantity}</td>
                              <td>{formatMoneyAmount(line.lineTotal, inv.currency)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-soft">
                              No line items
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        {inv.kind === "teleconsultation_settlement" && (
                          <>
                            <tr>
                              <td colSpan="3">Platform commission</td>
                              <td>{formatMoneyAmount(inv.platformCommissionAmount, inv.currency)}</td>
                            </tr>
                            {inv.partnerNetAmount != null && (
                              <tr>
                                <td colSpan="3">Partner net</td>
                                <td>{formatMoneyAmount(inv.partnerNetAmount, inv.currency)}</td>
                              </tr>
                            )}
                          </>
                        )}
                        <tr>
                          <td colSpan="3">Total</td>
                          <td>{formatMoneyAmount(inv.totalAmount, inv.currency)}</td>
                        </tr>
                      </tfoot>
                    </table>
                    <div className="nk-notes ff-italic fs-12px text-soft">
                      Electronic document — formal PDF in a later release.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Block>
        )}
      </Content>
    </div>
  );
};

export default InvoicePrint;
